/**
 * PageGuide TTS — always OpenAI shimmer via ttsService (matches classic voice engine).
 * Reuses unlockAudioContext + persistent HTMLAudioElement; never creates a new AudioContext per utterance.
 */

import {
  unlockAudioContext,
  prefetchSpeech,
  playPrefetchedAudio,
  stopSpeaking,
  isSpeaking,
  TTS_PLAYBACK_STOPPED_ERROR,
} from "services/ttsService";

export const PAGE_GUIDE_TTS_VOICE = "shimmer";

/**
 * Unlock audio inside a user-gesture handler (toggle-on, talk-back tap, tap-to-enable).
 */
export function unlockPageGuideAudio() {
  unlockAudioContext();
}

/**
 * Speak text with shimmer. Always show caption independently of this call.
 * @returns {Promise<'played'|'blocked'|'stopped'|'error'>}
 */
export async function speakPageGuideText(text, { onStart, onEnd, onBlocked, onError } = {}) {
  const trimmed = typeof text === "string" ? text.trim() : "";
  if (!trimmed) return "error";

  try {
    unlockAudioContext();
    const blob = await prefetchSpeech(trimmed, "en-US", {
      voice: PAGE_GUIDE_TTS_VOICE,
    });
    await playPrefetchedAudio(blob, {
      onStart,
      onEnd,
      onError: (err) => {
        if (err?.message === TTS_PLAYBACK_STOPPED_ERROR || err === TTS_PLAYBACK_STOPPED_ERROR) {
          return;
        }
        onError?.(err);
      },
    });
    return "played";
  } catch (err) {
    if (err?.message === TTS_PLAYBACK_STOPPED_ERROR || err === TTS_PLAYBACK_STOPPED_ERROR) {
      return "stopped";
    }
    const name = err?.name || "";
    const msg = String(err?.message || err || "");
    const blocked =
      name === "NotAllowedError" ||
      /notallowed|autoplay|user.?gesture|play\(\)/i.test(msg);
    if (blocked) {
      onBlocked?.(err);
      return "blocked";
    }
    onError?.(err);
    return "error";
  }
}

export function stopPageGuideSpeech() {
  stopSpeaking();
}

export function isPageGuideSpeaking() {
  return isSpeaking();
}
